import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2, MessageSquare, Send, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';

export function BulkSmsDialog({ open, onClose, leadIds, onSent }) {
  const [body, setBody] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [showMms, setShowMms] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.smsStatus().then(r => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, [open]);

  const handleSend = async () => {
    if (!body.trim()) { toast.error('Message body is required'); return; }
    if (!leadIds?.length) { toast.error('No leads selected'); return; }
    setSending(true);
    try {
      const { data } = await api.sendBulkSms(leadIds, body.trim(), mediaUrl.trim() || null);
      toast.success(`Sent ${data.sent} of ${data.requested} SMS messages`);
      setBody(''); setMediaUrl(''); setShowMms(false);
      if (onSent) onSent(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send bulk SMS');
    } finally {
      setSending(false);
    }
  };

  const count = leadIds?.length || 0;
  const charCount = body.length;
  const blocked = !status?.configured;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="bulk-sms-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="w-4 h-4 text-[#0047AB]" />
            Bulk SMS to {count} lead{count === 1 ? '' : 's'}
          </DialogTitle>
        </DialogHeader>
        {blocked && (
          <div className="p-3 rounded-sm border border-amber-200 bg-amber-50 text-xs text-amber-800">
            ClickSend is not yet configured on this server.
          </div>
        )}
        <div className="space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your message…"
            rows={5}
            maxLength={1600}
            disabled={blocked}
            className="text-sm rounded-sm"
            data-testid="bulk-sms-body"
          />
          {showMms && (
            <Input
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              placeholder="https://… (image URL, JPG/PNG/GIF, ≤250 kB)"
              className="text-sm rounded-sm"
              disabled={blocked}
              data-testid="bulk-sms-media"
            />
          )}
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs rounded-sm"
              onClick={() => setShowMms(v => !v)}
              disabled={blocked}
              data-testid="bulk-sms-toggle-mms-btn"
            >
              <ImageIcon className="w-3 h-3 mr-1" /> {showMms ? 'Drop media' : 'Attach media (MMS)'}
            </Button>
            <span className="text-[11px] text-slate-500">{charCount}/1600 · ~{Math.max(1, Math.ceil(charCount / 160))} part(s)/lead</span>
          </div>
          <p className="text-[11px] text-slate-500">
            Each lead will receive the same message. Bulk MMS sends one-by-one (slower).
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={sending} data-testid="bulk-sms-cancel-btn">
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-[#0047AB] hover:bg-[#003a8c]"
            onClick={handleSend}
            disabled={sending || blocked || !body.trim()}
            data-testid="bulk-sms-send-btn"
          >
            {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            Send to {count} lead{count === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
